import type { IAnyStateTreeNode, IMSTMap, IStateTreeNode, Instance } from "@gadgetinc/mobx-quick-tree";
import {
  compact,
  concat,
  difference,
  find,
  flatMap,
  intersection,
  isObject,
  map,
  memoize,
  sortBy,
  toLower,
  toUpper,
  uniq,
  upperFirst,
} from "lodash";
import { coerce, satisfies } from "semver";
import { legacyEnvironmentNameMatchesType, type EnvironmentType } from "../../Environment";
import type { FieldIndexEntry } from "../../IndexEntry";
import type { SourceFile } from "../../code/SourceFile";
import { ShopifyMetafieldFieldContributorID, ShopifyMetafieldModelRelations } from "../../contributors/shopify/ShopifyFieldContributor";
import { ModelField } from "../../database/ModelField";
import { getConnections, maybeGetConnections } from "../../database/utils";
import type { LiveTreeMap } from "../../live-tree/LiveTree";
import { isLiveTreeMap } from "../../live-tree/LiveTree";
import { getAppSettings } from "../../utils";
import { Connection } from "../Connection";
import { isShopifyConnection, isShopifyConnectionConfig, type ShopifyAppConfig } from "../ShopifyConnectionConfig";
import { INTERNAL_SHOPIFY_API_VERSION, ShopifyApiVersion } from "./ShopifyApiVersion";
import {
  ShopifyModelKeys,
  ShopifyModelMetadata,
  eachShopifyModelMetadataForVersion,
  eachShopifyModelMetadataForVersionWithModelKey,
  shopifyMetafieldFieldsForModel,
  shopifyModelMetadataForVersion,
  webhooksForMetadata,
  webhooksForVersion,
  type GraphQLSyncResource,
  type RESTSyncResource,
  type ShopifyWebhookConfig,
} from "./ShopifyModelMetadata";
import { ShopifyShopDisabledWebhooksKey, type RegisteredWebhooks } from "./ShopifyShop";
import { getShopifyModelApiIdentifierByModelKey } from "./apiIdentifiers";
import {
  DisallowedDisabledWebhookModels,
  ShopifyCoreModelKeys,
  ShopifySyncKey,
  isShopifyModelKey,
  shopifyModelKeyFromApiIdentifier,
} from "./keys";
import {
  CoreShopifyScopes,
  NO_REQUIRED_SCOPE,
  ShopifyAuthenticatedReadWriteScopes,
  ShopifyAuthenticatedSimpleScopes,
  ShopifyUnauthenticatedReadWriteScopes,
  ShopifyUnauthenticatedSimpleScopes,
  namespaceFromScope,
} from "./scopes";
import { webhooksWithoutModels, type ShopifyWebhookTopic } from "./webhooks";

/** Where in the contributor configuration we stash the identifier for a gadget field to map to a shopify payload key */
export const REST_SHOPIFY_CONTRIBUTOR_FIELD_KEY = "shopifyFieldKey";
export const GRAPHQL_SHOPIFY_CONTRIBUTOR_FIELD_KEY = "graphQLFieldKey";

export const isScopeId = (s: string) => allScopeIds.includes(s);
export const isReadWriteAuthenticatedScopeIdRead = (s: string) => readWriteAuthenticatedScopeIdsRead.includes(s);
export const isReadWriteAuthenticatedScopeIdWrite = (s: string) => readWriteAuthenticatedScopeIdsWrite.includes(s);
export const isReadWriteUnauthenticatedScopeIdRead = (s: string) => readWriteUnauthenticatedScopeIdsRead.includes(s);
export const isReadWriteUnauthenticatedScopeIdWrite = (s: string) => readWriteUnauthenticatedScopeIdsWrite.includes(s);
export const isSimpleScope = (s: string) => ShopifyAuthenticatedSimpleScopes.includes(s);

export const isAuthenticatedScope = (s: string) => s.startsWith("write_") || s.startsWith("read_");
export const isUnauthenticatedScope = (s: string) => s.startsWith("unauthenticated_write_") || s.startsWith("unauthenticated_read_");

export const stripPrefix = (name: string) => name.replace(/^(read_|write_)/, "");
export const toScopeNamespace = (scopeId: string): string => {
  return ShopifyAuthenticatedSimpleScopes.includes(scopeId) ? scopeId : stripPrefix(scopeId);
};

export const stripUnauthenticatedPrefix = (name: string) =>
  name.replace(/^(unauthenticated_read_|unauthenticated_write_|unauthenticated_)/, "");

export const readWriteAuthenticatedScopeIds = flatMap(ShopifyAuthenticatedReadWriteScopes, (namespace) => [
  `read_${namespace}`,
  `write_${namespace}`,
]);
const readWriteUnauthenticatedScopeIds = flatMap(ShopifyUnauthenticatedReadWriteScopes, (namespace) => [
  `unauthenticated_read_${stripUnauthenticatedPrefix(namespace)}`,
  `unauthenticated_write_${stripUnauthenticatedPrefix(namespace)}`,
]);
export const readWriteAuthenticatedScopeIdsRead = map(ShopifyAuthenticatedReadWriteScopes, (id) => `read_${id}`);
export const readWriteAuthenticatedScopeIdsWrite = map(ShopifyAuthenticatedReadWriteScopes, (id) => `write_${id}`);
export const readWriteUnauthenticatedScopeIdsRead = map(
  ShopifyUnauthenticatedReadWriteScopes,
  (id) => `unauthenticated_read_${stripUnauthenticatedPrefix(id)}`
);
export const readWriteUnauthenticatedScopeIdsWrite = map(
  ShopifyUnauthenticatedReadWriteScopes,
  (id) => `unauthenticated_write_${stripUnauthenticatedPrefix(id)}`
);
export const allScopeIds = concat(
  readWriteAuthenticatedScopeIds,
  readWriteUnauthenticatedScopeIds,
  ShopifyAuthenticatedSimpleScopes,
  ShopifyUnauthenticatedSimpleScopes
);

export const sortScopes = (scopes: string[]) => {
  return sortBy(scopes, [(name) => stripPrefix(name), (name) => name]);
};

export const getShopifyConnection = (node: IStateTreeNode) => {
  const connection = find(getConnections(node), (conn) => isShopifyConnection(conn));

  return connection && isShopifyConnection(connection) ? connection : undefined;
};

export const shopifyConnectionHasSyncOnlyModels = (node: IStateTreeNode) => {
  const connection = getShopifyConnection(node);

  if (!connection) return false;

  const config = connection.configuration;

  return config.enabledModelKeys.some((key) => isSyncOnlyModelKey(key, config.shopifyApiVersion));
};

/**
 * Return the scopes that are required to be granted to access a given model in the Shopify API
 *
 * Some models require multiple scopes to be accessed, and some models can be granted by one of many different sets of scopes. To support both these cases, this model returns an array of array of string scope names. Each top level array is a possible scope combination where the inner array of scopes must all be granted to give access.
 */
export const requiredScopeSetsForVersion = (modelKey: string, apiVersion: ShopifyApiVersion, write = false): string[][] => {
  const metadata = shopifyModelMetadataForVersion(modelKey, apiVersion);

  // these scopes must always be granted to access this model
  const requiredScopes = metadata?.requireExtraScopes ?? [];

  // one of any of these scopes can be granted to access this model
  const possibleAccessScopes = (metadata?.contributingScopes ?? [])
    .filter((scope) => scope != NO_REQUIRED_SCOPE)
    .map((s) => (ShopifyAuthenticatedReadWriteScopes.includes(s) ? (write ? `write_${s}` : `read_${s}`) : s));

  const scopeSets: string[][] = [];
  for (const possibleAccessScope of possibleAccessScopes) {
    const scopeSet = uniq([possibleAccessScope, ...requiredScopes]);
    if (scopeSet.length > 0) {
      scopeSets.push(scopeSet);
    }
  }

  return sortBy(scopeSets, "length");
};

export const requiredExtraModelKeysForVersion = (modelKey: string, apiVersion: ShopifyApiVersion) => {
  const metadata = shopifyModelMetadataForVersion(modelKey, apiVersion);
  return metadata?.requireExtraModelKeys ?? [];
};

/**
 * Return the default set of scopes that are required to be granted to access a set of given model in the Shopify API
 *
 * Some models can be accessed with a variety of different scope combinations -- this just gives one out of the many that might work.
 */
export const defaultRequiredScopesForVersion = (modelKeys: string[], apiVersion: ShopifyApiVersion): string[] => {
  return uniq(modelKeys.flatMap((modelKey) => requiredScopeSetsForVersion(modelKey, apiVersion)[0] ?? []));
};

export const scopeModelKeysForVersion = memoize((apiVersion: ShopifyApiVersion) => {
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
  const map = new Map<
    string,
    ((typeof ShopifyAuthenticatedReadWriteScopes)[number] | (typeof ShopifyAuthenticatedSimpleScopes)[number])[]
  >();
  for (const [modelKey, metadata] of eachShopifyModelMetadataForVersionWithModelKey(apiVersion)) {
    for (const scope of metadata.contributingScopes) {
      map.set(scope, [...(map.get(scope) ?? []), modelKey]);
    }
  }

  return map;
});

export const coreScopeWebhooksByVersion = (apiVersion: ShopifyApiVersion) =>
  webhookTopicsByModelKeyForVersion(
    CoreShopifyScopes.flatMap((scope) => scopeModelKeysForVersion(apiVersion).get(scope) ?? []),
    apiVersion
  );

export const getAncestorModelKeysForVersion = (startModelKey: string, apiVersion: ShopifyApiVersion): string[] => {
  const parentKeys: string[] = [];

  let m = shopifyModelMetadataForVersion(startModelKey, apiVersion);
  while (m?.parentKey && !parentKeys.includes(m.parentKey)) {
    const parentMetadata = shopifyModelMetadataForVersion(m.parentKey, apiVersion);
    if (!parentMetadata) {
      break;
    }

    parentKeys.push(m.parentKey);
    m = parentMetadata;
  }

  return parentKeys;
};

export const getDescendantModelKeysForVersion = (startModelKey: string, apiVersion: ShopifyApiVersion): string[] => {
  const childKeys: string[] = [];
  const modelMetadata = Object.fromEntries(eachShopifyModelMetadataForVersionWithModelKey(apiVersion));
  const addChildren = (modelKey: string) => {
    for (const key in modelMetadata) {
      if (modelMetadata[key].parentKey === modelKey) {
        childKeys.push(key);
        addChildren(key);
      }
    }
  };
  addChildren(startModelKey);
  return childKeys;
};

export function getShopifyAPIVersion(node: IAnyStateTreeNode): ShopifyApiVersion {
  const connections = maybeGetConnections(node) ?? [];
  return connections.reduceRight((version, conn) => {
    if ("shopifyApiVersion" in conn.configuration) {
      return conn.configuration.shopifyApiVersion;
    }
    return version;
  }, INTERNAL_SHOPIFY_API_VERSION);
}

export const getDisabledWebhookModelsFromShopRecord = (shop: any) => {
  let disabledWebhooks: any;

  if ("maybeGetByFieldKey" in shop) {
    disabledWebhooks = shop.maybeGetByFieldKey(ShopifyShopDisabledWebhooksKey);
  } else if ("disabledWebhooks" in shop) {
    disabledWebhooks = shop.disabledWebhooks;
  }

  const disabledWebhookModels = [];

  if (isObject(disabledWebhooks)) {
    for (const [apiIdentifier, disabled] of Object.entries(disabledWebhooks)) {
      const modelKey = shopifyModelKeyFromApiIdentifier(apiIdentifier);

      if (disabled && ShopifyModelKeys.includes(modelKey) && !DisallowedDisabledWebhookModels.includes(modelKey)) {
        disabledWebhookModels.push(modelKey);
      }
    }
  }

  return disabledWebhookModels;
};

export const isWebhookTriggeredModelKey = (modelKey: string, apiVersion: ShopifyApiVersion) => {
  const metadata = shopifyModelMetadataForVersion(modelKey, apiVersion);

  if (!metadata) return false;

  const hasWebhooks = Object.keys(metadata.webhooks).length > 0;

  return hasWebhooks;
};

export const isSyncOnlyModelKey = (modelKey: string, apiVersion: ShopifyApiVersion) => {
  if (modelKey === ShopifySyncKey || !isShopifyModelKey(modelKey)) return false;
  return !isWebhookTriggeredModelKey(modelKey, apiVersion);
};

export const requiredScopesByWebhookTopicsForVersion = (apiVersion: ShopifyApiVersion) => {
  const map: Record<string, string[]> = {};
  for (const metadata of eachShopifyModelMetadataForVersion(apiVersion)) {
    if (metadata.inheritWebhooks) {
      continue;
    }

    const scopes = uniq(
      concat(
        // read scope is sufficient to register webhooks for the associated model on Shopify
        metadata.contributingScopes.flatMap((scope) => (scope ? [`read_${scope}`] : [])),
        metadata.requireExtraScopes ?? []
      )
    );

    for (const [topic, config] of Object.entries(metadata.webhooks) as [ShopifyWebhookTopic, ShopifyWebhookConfig][]) {
      if (!config.skipRegistration) {
        map[topic] = uniq([...(map[topic] ?? []), ...scopes]);
      }
    }
  }

  return map;
};

export const validWebhookTopicsForConnection = (connection?: Connection) => {
  const connectionConfig = connection?.configuration;
  if (!connectionConfig || !isShopifyConnectionConfig(connectionConfig)) {
    return [];
  }

  const shopifyAPIVersion = getShopifyAPIVersion(connection);
  return availableWebhooksForScopes(connectionConfig.requestedScopesAsArray, shopifyAPIVersion);
};

export const webhookTopicsByModelKeyForVersion = (modelKeys: string[], apiVersion: ShopifyApiVersion) => {
  const topics: Record<string, ShopifyWebhookTopic[]> = {};
  for (const key of modelKeys) {
    topics[key] ??= [];

    const metadata = shopifyModelMetadataForVersion(key, apiVersion);
    if (metadata && !metadata.inheritWebhooks) {
      for (const [topic, config] of Object.entries(metadata.webhooks) as [ShopifyWebhookTopic, ShopifyWebhookConfig][]) {
        if (!config.skipRegistration && !topics[key].includes(topic)) {
          topics[key].push(topic);
        }
      }
    }
  }

  return topics;
};

export const getExpectedWebhooksForModelKeys = (
  modelKeys: string[],
  maybeGetModelByKey: (key: string) => { key: string; fieldsAsArray: FieldIndexEntry[] | ModelField[] } | undefined,
  apiVersion: ShopifyApiVersion
): RegisteredWebhooks => {
  const desiredTopics = webhookTopicsByModelKeyForVersion(modelKeys, apiVersion);
  const expectedWebhooks: RegisteredWebhooks = {};

  for (const [modelKey, potentialTopics] of Object.entries(desiredTopics)) {
    const model = maybeGetModelByKey(modelKey);

    let metafieldNamespaces: string[] = [];
    let privateMetafieldNamespaces: string[] = [];

    if (model) {
      metafieldNamespaces = getMetafieldNamespacesForModel(model, maybeGetModelByKey, apiVersion, false);
      privateMetafieldNamespaces = getMetafieldNamespacesForModel(model, maybeGetModelByKey, apiVersion, true);
    }

    potentialTopics.forEach((potentialTopic) => {
      expectedWebhooks[potentialTopic] = {
        namespaces: metafieldNamespaces,
        private_namespaces: privateMetafieldNamespaces,
      };
    });
  }

  return expectedWebhooks;
};

export type MissingWebhook =
  | {
      type: "topic";
      topic: string;
    }
  | {
      type: "namespaces";
      topic: string;
      namespaces: string[];
    }
  | {
      type: "private_namespaces";
      topic: string;
      private_namespaces: string[];
    };

export const getMissingWebhooks = (expectedWebhooks: RegisteredWebhooks, actualWebhooks: RegisteredWebhooks) => {
  const missingWebhooks: MissingWebhook[] = [];
  for (const [topic, { namespaces, private_namespaces }] of Object.entries(expectedWebhooks)) {
    if (!actualWebhooks[topic]) {
      missingWebhooks.push({ type: "topic", topic });
    } else {
      const namespacesDiff = difference(namespaces, actualWebhooks[topic].namespaces);
      const privateNamespacesDiff = difference(private_namespaces, actualWebhooks[topic].private_namespaces);
      if (namespacesDiff.length > 0) {
        missingWebhooks.push({ type: "namespaces", topic, namespaces: namespacesDiff });
      }
      if (privateNamespacesDiff.length > 0) {
        missingWebhooks.push({ type: "private_namespaces", topic, private_namespaces: privateNamespacesDiff });
      }
    }
  }
  return missingWebhooks;
};

export const getMissingWebhooksForShopRecord = (
  shop: any,
  enabledModelKeys: string[],
  registeredWebhooks: RegisteredWebhooks,
  maybeGetModelByKey: (key: string) => { key: string; fieldsAsArray: FieldIndexEntry[] | ModelField[] } | undefined,
  apiVersion: ShopifyApiVersion
) => {
  const disabledWebhookModelKeys = getDisabledWebhookModelsFromShopRecord(shop);

  const webhookModelKeys = uniq(enabledModelKeys).filter((modelKey) => !disabledWebhookModelKeys.includes(modelKey));

  const expectedWebhooks = getExpectedWebhooksForModelKeys(webhookModelKeys, maybeGetModelByKey, apiVersion);

  return getMissingWebhooks(expectedWebhooks, registeredWebhooks);
};

export type WebhookApiUpgradeItem = {
  topic: string;
  contributingScopes?: string[];
  modelKey: string;
  parentKey?: string;
  metaActionIdentifier: string;
};

export const groupedUpdatedWebhooks = (models: string[], currentApiVersion: ShopifyApiVersion, latestApiVersion: ShopifyApiVersion) => {
  const groupedWebhooks = models.reduce((result: Record<string, any>, model) => {
    const modelApiIdentifier = getShopifyModelApiIdentifierByModelKey(model, currentApiVersion);
    const currentMetadata = shopifyModelMetadataForVersion(model, currentApiVersion);
    const latestMetadata = shopifyModelMetadataForVersion(model, latestApiVersion);

    const currentWebhooks =
      currentMetadata?.inheritWebhooks !== true ? (Object.keys(currentMetadata?.webhooks ?? {}) as ShopifyWebhookTopic[]) : [];

    const latestWebhooks =
      latestMetadata?.inheritWebhooks !== true ? (Object.keys(latestMetadata?.webhooks ?? {}) as ShopifyWebhookTopic[]) : [];

    const deletedWebhookTopics = difference<ShopifyWebhookTopic>(currentWebhooks, latestWebhooks).map(
      (webhookTopic) =>
        ({
          topic: webhookTopic,
          contributingScopes: latestMetadata?.contributingScopes,
          modelKey: model,
          parentKey: latestMetadata?.parentKey,
          metaActionIdentifier: latestMetadata?.webhooks?.[webhookTopic]?.metaActionIdentifier,
        } as WebhookApiUpgradeItem)
    );

    const addedWebhookTopics = difference<ShopifyWebhookTopic>(latestWebhooks, currentWebhooks).map(
      (webhookTopic) =>
        ({
          topic: webhookTopic,
          contributingScopes: latestMetadata?.contributingScopes,
          modelKey: model,
          parentKey: latestMetadata?.parentKey,
          metaActionIdentifier: latestMetadata?.webhooks?.[webhookTopic]?.metaActionIdentifier,
        } as WebhookApiUpgradeItem)
    );

    if (addedWebhookTopics.length > 0 || deletedWebhookTopics.length > 0) {
      result[modelApiIdentifier] = { Added: [...addedWebhookTopics], Deleted: [...deletedWebhookTopics] };
    }

    return result;
  }, {});

  return groupedWebhooks;
};

export const getMetafieldNamespacesForModel = (
  model: { key: string; fieldsAsArray: FieldIndexEntry[] | ModelField[] },
  maybeGetModelByKey: (key: string) => { key: string; fieldsAsArray: FieldIndexEntry[] | ModelField[] } | undefined,
  apiVersion: ShopifyApiVersion,
  isPrivate = false
): string[] => {
  const namespaces = shopifyMetafieldFieldsForModel(model, apiVersion, isPrivate).map(
    (f) => f.contributorConfiguration[ShopifyMetafieldFieldContributorID]?.metafield?.namespace
  );

  const relatedModelKeys = ShopifyMetafieldModelRelations[model.key];
  if (relatedModelKeys) {
    for (const relatedModelKey of relatedModelKeys) {
      const relatedModel = maybeGetModelByKey(relatedModelKey);
      if (relatedModel) namespaces.push(...getMetafieldNamespacesForModel(relatedModel, maybeGetModelByKey, apiVersion, isPrivate));
    }
  }

  return uniq(compact(namespaces));
};

export const isShopifyGraphQLTopic = (topic: string): boolean => topic.split("").every((char) => /[A-Z_]/.test(char));

const knownRestPrefixes = memoize(() =>
  sortBy(
    uniq([
      ...Object.values(ShopifyModelMetadata)
        .flatMap((metadata) => Object.keys(metadata.webhooks))
        .map((topic) => topic.split("/")[0]),
      "customers_marketing_consent",
      "customers_email_marketing_consent",
      "variants",
      "reverse_deliveries",
      "product_feeds",
      "audit_events",
    ]),
    (value) => 0 - value.length
  )
);

export const toShopifyRESTWebhookTopic = (topic: string) => {
  if (isShopifyGraphQLTopic(topic)) {
    if (topic == "CUSTOMER_TAGS_ADDED") {
      return "customer.tags_added";
    } else if (topic == "CUSTOMER_TAGS_REMOVED") {
      return "customer.tags_removed";
    } else {
      topic = toLower(topic);
      let namespaceIndex: number;
      const knownPrefix = knownRestPrefixes().find((prefix) => topic.startsWith(prefix));

      if (knownPrefix) {
        namespaceIndex = knownPrefix.length;
      } else {
        namespaceIndex = topic.lastIndexOf("_");
      }
      return (topic.substring(0, namespaceIndex) + "/" + topic.substring(namespaceIndex + 1)) as ShopifyWebhookTopic;
    }
  } else {
    return topic as ShopifyWebhookTopic;
  }
};

export const toShopifyGraphQLWebhookTopic = (topic: string) => {
  if (isShopifyGraphQLTopic(topic)) {
    return topic;
  } else {
    return toUpper(topic).replace(/\/|\./, "_");
  }
};

export const normalizeScopes = (scopes: string[]) => {
  return Array.from(
    new Set(
      scopes.filter((scope) => {
        if (scope.startsWith("write") || scope.startsWith("unauthenticated_write")) {
          return true;
        }
        // Keep read scopes only if there's no corresponding write scope
        if (scope.startsWith("read_")) {
          const writeScope = `write_${scope.slice(5)}`;
          return !scopes.includes(writeScope);
        }
        if (scope.startsWith("unauthenticated_read_")) {
          const writeScope = `unauthenticated_write_${scope.slice(21)}`;
          return !scopes.includes(writeScope);
        }
        return false;
      })
    )
  ).sort();
};

export const denormalizeScopes = (scopes: string[]) => {
  const allScopes = scopes.flatMap((scope) => {
    if (scope.startsWith("write")) {
      if (ShopifyAuthenticatedSimpleScopes.includes(scope)) {
        return scope;
      }
      const resource = scope.replace(/^write_/, "");
      if (ShopifyAuthenticatedReadWriteScopes.includes(resource)) {
        return [scope, `read_${resource}`];
      }
    }
    if (scope.startsWith("unauthenticated_write")) {
      if (ShopifyUnauthenticatedSimpleScopes.includes(scope)) {
        return scope;
      }
      const resource = scope.replace(/^unauthenticated_write_/, "");
      return [scope, `unauthenticated_read_${resource}`];
    }
    return scope;
  });
  return Array.from(new Set(allScopes)).sort();
};

export const getMissingScopes = (requiredScopes: string[], grantedScopes: string[]) => {
  return requiredScopes.filter((scope) => {
    if (grantedScopes.includes(scope)) return false;

    if (scope.startsWith("read_") && grantedScopes.includes(`write_${scope.slice(scope.indexOf("_") + 1)}`)) {
      return false;
    }

    return true;
  });
};

/** Given a list of read/write scope namespaces or simple scopes, return real Shopify shop ids for each, adding the read_ prefix to any namespaces */
export const exampleScopes = (scopesOrNamespaces: string[]) => {
  return scopesOrNamespaces.map((s) => (isScopeId(s) ? s : `read_${s}`));
};

export const findHighestPrecedenceCoreShopifyModelKey = (modelKeys: string[]): string | undefined => {
  const coreModelKeys = modelKeys.filter((key) => ShopifyCoreModelKeys.includes(key));
  const coreModelKeysByPredence = sortBy(uniq(coreModelKeys), (key) => {
    const index = ShopifyCoreModelKeys.indexOf(key);
    return index == -1 ? Number.MAX_SAFE_INTEGER : index;
  });

  return coreModelKeysByPredence[0];
};

const SyncResourceToShopifyApiIdentifier: Partial<{ [K in GraphQLSyncResource | RESTSyncResource]: string }> = {
  "customer.paymentMethods": "shopifyCustomerPaymentMethod",
  files: "shopifyFile",
  giftCards: "shopifyGiftCard",
  subscriptionContracts: "shopifySubscriptionContract",
  "subscriptionContract.billingAttempts": "shopifySubscriptionBillingAttempt",
  "subscriptionContract.lines": "shopifySubscriptionLine",
  "subscriptionContract.discounts": "shopifySubscriptionManualDiscount",
  "sellingPlanGroup.sellingPlans": "shopifySellingPlan",
  sellingPlanGroups: "shopifySellingPlanGroup",
  markets: "shopifyMarket",
  "market.regions": "shopifyMarketRegion",
  companies: "shopifyCompany",
  companyLocations: "shopifyCompanyLocation",
  "companyLocation.roleAssignments": "shopifyCompanyContactRoleAssignment",
  "company.contactRoles": "shopifyCompanyContactRole",
  "company.contacts": "shopifyCompanyContact",
  discountNodes: "shopifyDiscount",
  smartCollection: "shopifyCollection",
  customCollection: "shopifyCollection",
  balance: "shopifyBalanceTransaction",
  inventory: "shopifyInventoryLevel",
  transaction: "shopifyOrderTransaction",
  products: "shopifyProduct",
  "product.variants": "shopifyProductVariant",
  "product.media": "shopifyProductMedia",
  "productVariant.media": "shopifyProductVariantMedia",
  draftOrders: "shopifyDraftOrder",
  "draftOrder.lineItems": "shopifyDraftOrderLineItem",
  businessEntities: "shopifyBusinessEntity",
  shopifyPaymentsAccount: "shopifyPaymentsAccount",
};

export const shopifyApiIdentifierForSyncResource = (resource: GraphQLSyncResource | RESTSyncResource) => {
  return SyncResourceToShopifyApiIdentifier[resource] ?? `shopify${upperFirst(resource)}`;
};

export const appConfigMatchesEnvironmentType = (appConfig: Instance<typeof ShopifyAppConfig>, environmentType: EnvironmentType) => {
  if ("environment" in appConfig) {
    return legacyEnvironmentNameMatchesType(appConfig.environment, environmentType);
  } else {
    const appSettings = getAppSettings(appConfig);

    return appSettings.environmentType === environmentType;
  }
};

export const hasAppBridgeV4 = async (root: { files: IMSTMap<typeof SourceFile> | Instance<LiveTreeMap<typeof SourceFile>> }) => {
  let file: Instance<typeof SourceFile> | undefined;
  if (isLiveTreeMap(root.files)) {
    file = await root.files.get("package.json").fetch();
  } else {
    file = root.files.get("package.json") as Instance<typeof SourceFile>;
  }

  if (!file) {
    return false;
  }

  return packageJsonHasAppBridgeV4(file.data);
};

export const packageJsonHasAppBridgeV4 = (fileContent: string) => {
  try {
    const json = JSON.parse(fileContent);
    const packageJsonVersion = coerce(json.dependencies["@shopify/app-bridge-react"])?.version;

    if (!packageJsonVersion) {
      return false;
    }

    return satisfies(packageJsonVersion, ">=4.0.0");
  } catch (e) {
    return false;
  }
};

export const availableWebhooksForScopes = (scopes: string[], apiVersion: ShopifyApiVersion) => {
  const versionWebhooks = webhooksForVersion(apiVersion);
  const scopeModelKeys = scopeModelKeysForVersion(apiVersion);
  const enabledWebhooks = uniq([
    ...scopes.flatMap((scope) =>
      (scopeModelKeys.get(namespaceFromScope(scope)) ?? []).flatMap((key) =>
        Object.keys(webhooksForMetadata(ShopifyModelMetadata[key], apiVersion))
      )
    ),
    ...(scopeModelKeys.get("") ?? []).flatMap((key) => Object.keys(webhooksForMetadata(ShopifyModelMetadata[key], apiVersion))),
    ...intersection(Array.from(webhooksWithoutModels.keys()), versionWebhooks).filter((topic) => {
      const webhookScopes = webhooksWithoutModels.get(topic as ShopifyWebhookTopic);
      return intersection(webhookScopes, scopes).length > 0 || webhookScopes?.length === 0;
    }),
  ]);

  return intersection(versionWebhooks, enabledWebhooks);
};

export const availableWebhooksForModels = (modelKeys: string[], apiVersion: ShopifyApiVersion) => {
  let webhookTopics: string[] = [];

  const webhookTopicsByModelKey = webhookTopicsByModelKeyForVersion(modelKeys, apiVersion);

  for (const modelKey of Object.keys(webhookTopicsByModelKey)) {
    webhookTopics = uniq([...webhookTopics, ...webhookTopicsByModelKey[modelKey]]);
  }

  return webhookTopics;
};

export const findAppConfigForApiKey = (root: { connectionsAsArray: Connection[] }, apiKey: string) => {
  return root.connectionsAsArray
    .flatMap((connection) => {
      const connectionConfig = connection.configuration;

      if (!isShopifyConnectionConfig(connectionConfig)) {
        return [];
      }

      return [...connectionConfig.appConfigsAsArray];
    })
    .find((app) => app.apiKey == apiKey);
};

export const shopifyCurrencyCodeEnumOptions = [
  { name: "AED" },
  { name: "AFN" },
  { name: "ALL" },
  { name: "AMD" },
  { name: "ANG" },
  { name: "AOA" },
  { name: "ARS" },
  { name: "AUD" },
  { name: "AWG" },
  { name: "AZN" },
  { name: "BAM" },
  { name: "BBD" },
  { name: "BDT" },
  { name: "BGN" },
  { name: "BHD" },
  { name: "BIF" },
  { name: "BMD" },
  { name: "BND" },
  { name: "BOB" },
  { name: "BRL" },
  { name: "BSD" },
  { name: "BTN" },
  { name: "BWP" },
  { name: "BYN" },
  { name: "BYR" },
  { name: "BZD" },
  { name: "CAD" },
  { name: "CDF" },
  { name: "CHF" },
  { name: "CLP" },
  { name: "CNY" },
  { name: "COP" },
  { name: "CRC" },
  { name: "CVE" },
  { name: "CZK" },
  { name: "DJF" },
  { name: "DKK" },
  { name: "DOP" },
  { name: "DZD" },
  { name: "EGP" },
  { name: "ERN" },
  { name: "ETB" },
  { name: "EUR" },
  { name: "FJD" },
  { name: "FKP" },
  { name: "GBP" },
  { name: "GEL" },
  { name: "GHS" },
  { name: "GIP" },
  { name: "GMD" },
  { name: "GNF" },
  { name: "GTQ" },
  { name: "GYD" },
  { name: "HKD" },
  { name: "HNL" },
  { name: "HRK" },
  { name: "HTG" },
  { name: "HUF" },
  { name: "IDR" },
  { name: "ILS" },
  { name: "INR" },
  { name: "IQD" },
  { name: "IRR" },
  { name: "ISK" },
  { name: "JEP" },
  { name: "JMD" },
  { name: "JOD" },
  { name: "JPY" },
  { name: "KES" },
  { name: "KGS" },
  { name: "KHR" },
  { name: "KID" },
  { name: "KMF" },
  { name: "KRW" },
  { name: "KWD" },
  { name: "KYD" },
  { name: "KZT" },
  { name: "LAK" },
  { name: "LBP" },
  { name: "LKR" },
  { name: "LRD" },
  { name: "LSL" },
  { name: "LTL" },
  { name: "LVL" },
  { name: "LYD" },
  { name: "MAD" },
  { name: "MDL" },
  { name: "MGA" },
  { name: "MKD" },
  { name: "MMK" },
  { name: "MNT" },
  { name: "MOP" },
  { name: "MRU" },
  { name: "MUR" },
  { name: "MVR" },
  { name: "MWK" },
  { name: "MXN" },
  { name: "MYR" },
  { name: "MZN" },
  { name: "NAD" },
  { name: "NGN" },
  { name: "NIO" },
  { name: "NOK" },
  { name: "NPR" },
  { name: "NZD" },
  { name: "OMR" },
  { name: "PAB" },
  { name: "PEN" },
  { name: "PGK" },
  { name: "PHP" },
  { name: "PKR" },
  { name: "PLN" },
  { name: "PYG" },
  { name: "QAR" },
  { name: "RON" },
  { name: "RSD" },
  { name: "RUB" },
  { name: "RWF" },
  { name: "SAR" },
  { name: "SBD" },
  { name: "SCR" },
  { name: "SDG" },
  { name: "SEK" },
  { name: "SGD" },
  { name: "SHP" },
  { name: "SLL" },
  { name: "SOS" },
  { name: "SRD" },
  { name: "SSP" },
  { name: "STD" },
  { name: "SYP" },
  { name: "SZL" },
  { name: "THB" },
  { name: "TJS" },
  { name: "TMT" },
  { name: "TND" },
  { name: "TOP" },
  { name: "TRY" },
  { name: "TTD" },
  { name: "TWD" },
  { name: "TZS" },
  { name: "UAH" },
  { name: "UGX" },
  { name: "USD" },
  { name: "UYU" },
  { name: "UZS" },
  { name: "VEF" },
  { name: "VES" },
  { name: "VND" },
  { name: "VUV" },
  { name: "WST" },
  { name: "XAF" },
  { name: "XCD" },
  { name: "XOF" },
  { name: "XPF" },
  { name: "XXX" },
  { name: "YER" },
  { name: "ZAR" },
  { name: "ZMW" },
];
